import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isBackendAuthEnabled } from "@/lib/authApi";
import { cognitoUserPool, isLocalDevBypassEnabled } from "@/lib/cognitoPool";
import { setPermission403Handler } from "@/lib/permission403";
import { permissionKey } from "@/lib/rbacCatalog";
import {
  ALL_SCOPE_NAMES,
  fullScopeName,
  hasAnyHaloScope,
  hasScopeForPermission,
  parseScopesFromAccessToken,
} from "@/lib/scopeAuth";
import { addSessionRefreshListener } from "@/lib/sessionRefresh";
import {
  getCognitoAccessToken,
  getIdpScopes,
  setCognitoAccessToken,
  setCognitoIdToken,
} from "@/lib/tokenStore";

const REFRESH_MS = 60 * 1000;

const PermissionsContext = createContext(null);

function scopesFromStore() {
  const fromToken = parseScopesFromAccessToken(getCognitoAccessToken());
  if (fromToken.size > 0) return fromToken;
  const stored = getIdpScopes();
  if (stored.length > 0) return new Set(stored);
  return fromToken;
}

function scopesEqual(a, b) {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const scope of a) {
    if (!b.has(scope)) return false;
  }
  return true;
}

export function PermissionsProvider({ children }) {
  const {
    isAuthenticated,
    loading: authLoading,
    groups: authGroups,
    logout,
  } = useAuth();
  const [scopeSet, setScopeSet] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshRef = useRef(0);

  const applyDevBypass = useCallback(() => {
    setScopeSet(new Set(ALL_SCOPE_NAMES));
    setError(null);
    setLoading(false);
  }, []);

  const clearPermissions = useCallback(() => {
    setScopeSet(new Set());
    setError(null);
    setLoading(false);
  }, []);

  /** Cognito session dead (refresh 400, revoked, expired) — end SPA session, not only scopes. */
  const endSessionAfterAuthFailure = useCallback(() => {
    clearPermissions();
    logout();
  }, [clearPermissions, logout]);

  const applyScopesFromToken = useCallback(() => {
    const scopes = scopesFromStore();
    setScopeSet((prev) => (scopesEqual(prev, scopes) ? prev : scopes));
    setError(null);
    setLoading(false);
    return scopes;
  }, []);

  const syncScopesFromStore = useCallback(() => {
    const scopes = scopesFromStore();
    if (scopes.size > 0) {
      setScopeSet((prev) => (scopesEqual(prev, scopes) ? prev : scopes));
      setError(null);
      setLoading(false);
    }
    return scopes;
  }, []);

  const refreshPermissions = useCallback(() => {
    if (!isAuthenticated) {
      clearPermissions();
      return Promise.resolve();
    }
    if (isLocalDevBypassEnabled()) {
      applyDevBypass();
      return Promise.resolve();
    }
    if (isBackendAuthEnabled()) {
      applyScopesFromToken();
      return Promise.resolve();
    }
    if (!cognitoUserPool) {
      applyScopesFromToken();
      return Promise.resolve();
    }

    const requestId = ++refreshRef.current;
    const alreadyHaveScopes = scopeSet.size > 0 || scopesFromStore().size > 0;
    if (!alreadyHaveScopes) {
      setLoading(true);
    }
    setError(null);

    return new Promise((resolve) => {
      const cognitoUser = cognitoUserPool.getCurrentUser();
      if (!cognitoUser) {
        // Tokens may already be in memory from AuthContext.login before SDK persists getCurrentUser.
        if (getCognitoAccessToken()) {
          applyScopesFromToken();
          resolve();
          return;
        }
        if (requestId === refreshRef.current) {
          endSessionAfterAuthFailure();
        }
        resolve();
        return;
      }
      cognitoUser.getSession((err, session) => {
        if (requestId !== refreshRef.current) {
          resolve();
          return;
        }
        if (err || !session?.isValid()) {
          endSessionAfterAuthFailure();
          resolve();
          return;
        }
        setCognitoAccessToken(session.getAccessToken().getJwtToken());
        setCognitoIdToken(session.getIdToken().getJwtToken());
        applyScopesFromToken();
        resolve();
      });
    });
  }, [
    applyDevBypass,
    applyScopesFromToken,
    clearPermissions,
    endSessionAfterAuthFailure,
    isAuthenticated,
    scopeSet.size,
  ]);

  useEffect(() => {
    return addSessionRefreshListener(() => {
      applyScopesFromToken();
    });
  }, [applyScopesFromToken]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      clearPermissions();
      return;
    }
    if (isLocalDevBypassEnabled()) {
      applyDevBypass();
      return;
    }
    // AuthContext sets access token synchronously on login — use it before async getSession.
    const immediate = syncScopesFromStore();
    if (immediate.size === 0) {
      setLoading(true);
    }
    refreshPermissions();
  }, [
    authLoading,
    isAuthenticated,
    refreshPermissions,
    clearPermissions,
    applyDevBypass,
    syncScopesFromStore,
  ]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onFocus = () => {
      refreshPermissions();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isAuthenticated, refreshPermissions]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const id = window.setInterval(refreshPermissions, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [isAuthenticated, refreshPermissions]);

  useEffect(() => {
    setPermission403Handler(() => {
      refreshPermissions();
    });
    return () => setPermission403Handler(null);
  }, [refreshPermissions]);

  const hasPermission = useCallback(
    (resource, action) => {
      if (isLocalDevBypassEnabled() && isAuthenticated) return true;
      return hasScopeForPermission(scopeSet, resource, action);
    },
    [isAuthenticated, scopeSet],
  );

  const hasPermissionKey = useCallback(
    (key) => {
      const idx = key.indexOf(":");
      if (idx === -1) return false;
      return hasPermission(key.slice(0, idx), key.slice(idx + 1));
    },
    [hasPermission],
  );

  const hasScope = useCallback(
    (shortName) => scopeSet.has(shortName),
    [scopeSet],
  );

  const hasAnyAccess = useMemo(
    () =>
      hasAnyHaloScope(scopeSet)
      || (isLocalDevBypassEnabled() && isAuthenticated),
    [isAuthenticated, scopeSet],
  );

  const scopesPending =
    isAuthenticated &&
    !isLocalDevBypassEnabled() &&
    loading &&
    !hasAnyHaloScope(scopeSet);

  const value = useMemo(
    () => ({
      groups: authGroups,
      permissions: scopeSet,
      scopes: scopeSet,
      loading: authLoading || loading || scopesPending,
      error,
      hasAnyAccess,
      hasPermission,
      hasPermissionKey,
      hasScope,
      refreshPermissions,
      clearPermissions,
    }),
    [
      authGroups,
      scopeSet,
      authLoading,
      loading,
      scopesPending,
      error,
      hasAnyAccess,
      hasPermission,
      hasPermissionKey,
      hasScope,
      refreshPermissions,
      clearPermissions,
    ],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error("usePermissions must be used within PermissionsProvider");
  }
  return ctx;
}

/** @deprecated use hasScope — kept for gradual migration */
export function scopeKey(resource, action) {
  return permissionKey(resource, action);
}

export { fullScopeName };
